import { Box, Flex, useToast } from '@chakra-ui/react';
import { EmptyDataButton } from '@texas/components/shared/button/EmptyDataButton';
import { CompositionComponent } from './CompositionComponent';
import { Icons } from '@texas/components/shared/Icons';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import {
  Composition,
  compositionApi,
} from '@texas/api/endpoints/compositionApi';
import { request } from '@texas/utils/helpers/httpHelpers';
import { compositionEvents } from '@texas/components/shared/composition/events';
import { useTranslation } from 'react-i18next';

interface OptionComponent {
  hasAnyBasedOn: boolean;
  isReady: boolean;
  compositions: Composition[];
  id: number;
  template: boolean;
}

export function OptionsComponent({
  option,
  isMatrix,
  isVdc,
}: {
  option: OptionComponent;
  isMatrix: boolean;
  isVdc: boolean;
}) {
  return (
    <Box
      sx={{
        container: 'options / inline-size',
      }}
    >
      <Flex
        flexGrow={1}
        gap={2}
        p={2}
        display="flex"
        sx={{
          '@container options (max-width: 500px)': {
            flexDirection: 'column',
          },
          '@container options (min-width: 501px)': {
            flexDirection: 'row',
            overflowX: 'auto',
          },
        }}
      >
        {option.compositions.map((x, i) => {
          return (
            <CompositionComponent
              groupHasAnyBasedOn={option.hasAnyBasedOn}
              groupId={option.id}
              disableArchive={option.hasAnyBasedOn}
              isMatrix={isMatrix}
              isReady={option.isReady}
              key={x.id}
              index={i}
              isVdc={isVdc}
              composition={x}
              vdcTemplate={option.template}
              onMaterialChanged={() =>
                compositionEvents.onMaterialChanged.dispatch()
              }
            />
          );
        })}
        {!option.hasAnyBasedOn && !isVdc && (
          <Box px={8} pt={6} textAlign="center">
            <NewComposition
              groupId={option.id}
              isReady={option.isReady}
              onAdd={() => compositionEvents.onAdd.dispatch()}
            />
          </Box>
        )}
      </Flex>
    </Box>
  );
}

function NewComposition({
  groupId,
  isReady,
  onAdd,
}: {
  groupId: number;
  isReady: boolean;
  onAdd: () => void;
}) {
  const { t } = useTranslation();
  const { request: addComposition, loading } = useApiRequest(
    compositionApi.addComposition,
  );
  const toast = useToast();

  const performRequest = async () =>
    await request(
      addComposition,
      [groupId],
      () => onAdd(),
      (error) => {
        toast({
          title: t('general.actionFailed'),
          status: 'error',
          description: error.message,
          isClosable: true,
        });
      },
    );

  return (
    <EmptyDataButton
      variant="large-center"
      icon={<Icons.LayersPlus boxSize={8} />}
      isLoading={loading}
      onClick={async () => {
        if (loading) return;
        await performRequest();
      }}
      label={t('composition.new')}
      description={
        isReady
          ? t('composition.clickToAddAndRevertIsReady')
          : t('composition.clickToAdd')
      }
    />
  );
}
